import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import SplashScreen from "./components/SplashScreen";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import Home from "./pages/Home";
import ProjectsPage from "./pages/ProjectsPage";
import ProjectDetails from "./pages/ProjectDetails";
import About from "./pages/About";
import Dienstleistungen from "./pages/Dienstleistungen";
import DienstleistungDetail from "./pages/DienstleistungDetail";
import Contact from "./pages/Contact";
import Impressum from "./pages/Impressum";
import Datenschutz from "./pages/Datenschutz";
import NotFound from "./pages/NotFound";
import FloatingButtons from "./components/FloatingButtons";
import NewsPage from "./pages/NewsPage";
import NewsDetailPage from "./pages/NewsDetailPage";
import Jobs from "./pages/Jobs";
import JobDetails from "./pages/JobDetails";

const getNavTypeSafe = ():
  | "navigate"
  | "reload"
  | "back_forward"
  | "prerender"
  | "unknown" => {
  if (typeof performance === "undefined") return "unknown";

  try {
    const anyPerf = performance as any;
    const entries: PerformanceNavigationTiming[] | undefined =
      anyPerf.getEntriesByType?.("navigation");
    const entry = Array.isArray(entries)
      ? (entries[0] as PerformanceNavigationTiming)
      : undefined;

    if (entry?.type) return entry.type as any;

    const legacy = (performance as any).navigation?.type;
    if (legacy === 1) return "reload";
    if (legacy === 2) return "back_forward";
    if (legacy === 0) return "navigate";

    return "unknown";
  } catch {
    return "unknown";
  }
};

const useInitialSplashDecision = (isHomeNow: boolean) => {
  const decidedRef = useRef<null | boolean>(null);
  if (decidedRef.current !== null) return decidedRef.current;

  const navType = getNavTypeSafe();

  const connection =
    typeof navigator !== "undefined" && (navigator as any)?.connection
      ? (navigator as any).connection
      : null;

  const saveData = !!connection?.saveData;

  const prefersReduced =
    typeof window !== "undefined" &&
    !!window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;

  const show =
    isHomeNow &&
    (navType === "navigate" || navType === "reload") &&
    !(saveData || prefersReduced);

  decidedRef.current = show;
  return show;
};


type ReadyEvent = "loadedmetadata" | "loadeddata" | "canplay" | "canplaythrough";

type SplashCfg = {
  delayMs: number;
  maxWaitMs: number;
  images?: string[];
  videos?: string[];
  videoReadyEvent?: ReadyEvent;
  waitForVideo?: boolean;
};

const canAutoplayAmbient = () => {
  const c: any =
    typeof navigator !== "undefined" ? (navigator as any)?.connection : null;

  const saveData = !!c?.saveData;
  const effectiveType = c?.effectiveType;

  const prefersReduced =
    typeof window !== "undefined" &&
    !!window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;

  return (
    !prefersReduced &&
    !saveData &&
    (!effectiveType || effectiveType === "4g")
  );
};

const pickLevel = (): "720" | "1080" => {
  const c: any =
    typeof navigator !== "undefined" ? (navigator as any)?.connection : null;

  const saveData = !!c?.saveData;
  const effectiveType = c?.effectiveType;
  const downlink = typeof c?.downlink === "number" ? c.downlink : undefined;

  const isMobile =
    typeof window !== "undefined"
      ? window.matchMedia("(max-width:820px)").matches
      : false;

  const isWide =
    typeof window !== "undefined"
      ? window.matchMedia("(min-width:1200px)").matches
      : false;

  if (saveData) return "720";
  if (isMobile) return "720";
  if (effectiveType && effectiveType !== "4g") return "720";
  if (downlink !== undefined && downlink < 8) return "720";
  return isWide ? "1080" : "720";
};

const isLightRoute = (path: string) =>
  path === "/contact" ||
  path === "/impressum" ||
  path === "/datenschutz" ||
  path.startsWith("/aktuelles");

const getCfgForPath = (path: string): SplashCfg | null => {
  if (isLightRoute(path)) return null;

  if (path === "/") {
    const level = pickLevel();
    return {
      delayMs: 1500,
      maxWaitMs: 3500,
      images: ["/media/video-light/posters/hero1.jpg"],
      videos: [`/media/video-light/mp4/${level}/hero-${level}.mp4`],
      videoReadyEvent: "loadedmetadata",
      waitForVideo: true,
    };
  }

  if (path === "/about") {
    const level = pickLevel();
    return {
      delayMs: 500,
      maxWaitMs: 3500,
      images: ["/media/video-light/about/posters/IMG_1002.jpg"],
      videos: [`/media/video-light/about/${level}/IMG_1002-${level}.mp4`],
      videoReadyEvent: "loadedmetadata",
      waitForVideo: true,
    };
  }

  if (path === "/projects") {
    return {
      delayMs: 500,
      maxWaitMs: 2500,
      images: ["/media/projects/dorint/1600/mueritz.webp"],
    };
  }

  if (path.startsWith("/projects/") && path.split("/").length === 3) {
    return { delayMs: 700, maxWaitMs: 2200 };
  }

  if (path === "/dienstleistungen") {
    return { delayMs: 500, maxWaitMs: 2500 };
  }

  if (path.startsWith("/dienstleistungen/") && path.split("/").length === 3) {
    return { delayMs: 700, maxWaitMs: 2200 };
  }

  return null;
};

const setSplashFlag = (on: boolean) => {
  if (typeof document === "undefined") return;
  const el = document.documentElement;

  if (on) {
    el.classList.add("splash-active");
    el.setAttribute("data-splash", "1");
  } else {
    el.classList.remove("splash-active");
    el.setAttribute("data-splash", "0");
  }
};

const AppContent: React.FC = () => {
  const location = useLocation();
  const isHome = location.pathname === "/";

  const initialSplash = useInitialSplashDecision(isHome);
  const [showSplash, setShowSplash] = useState<boolean>(initialSplash);
  const splashMode: "reveal" | "instant" =
    location.pathname === "/" ? "reveal" : "instant";

  const activePathRef = useRef(location.pathname);


  useLayoutEffect(() => {
    const boot = document.getElementById("boot-splash");
    if (!boot) return;
    document.documentElement.classList.remove("splash-boot");
    boot.remove();
  }, []);

  useLayoutEffect(() => {
    const path = location.pathname;
    activePathRef.current = path;

    const cfg = getCfgForPath(path);

    if (!cfg) {
      setShowSplash(false);
      setSplashFlag(false);
      return;
    }

    const navType = getNavTypeSafe();
    if (navType === "back_forward") {
      setShowSplash(false);
      setSplashFlag(false);
      return;
    }

    setSplashFlag(true);
    setShowSplash(true);

    let cancelled = false;
    let done = 0;

    const images = (cfg.images ?? []).filter(Boolean);
    const videos = (cfg.videos ?? []).filter(Boolean);

    const waitVideo =
      !!cfg.waitForVideo && videos.length > 0 && canAutoplayAmbient();

    const total = images.length + (waitVideo ? videos.length : 0);

    const timers: number[] = [];
    const cleanups: Array<() => void> = [];

    const close = () => {
      if (cancelled) return;
      setShowSplash(false);
      setSplashFlag(false);
    };

    const finish = () => {
      if (cancelled) return;
      const t = window.setTimeout(() => {
        if (!cancelled) close();
      }, cfg.delayMs);
      timers.push(t);
    };

    const check = () => {
      if (cancelled) return;
      if (total === 0) return finish();
      if (done >= total) finish();
    };

    const hard = window.setTimeout(() => {
      if (!cancelled) close();
    }, cfg.maxWaitMs);
    timers.push(hard);

    images.forEach((src) => {
      const img = new Image();
      const onDone = () => {
        done += 1;
        check();
      };
      img.addEventListener("load", onDone, { once: true });
      img.addEventListener("error", onDone, { once: true });
      img.decoding = "async";
      img.src = src;

      cleanups.push(() => {
        img.removeEventListener("load", onDone);
        img.removeEventListener("error", onDone);
      });
    });

    if (waitVideo) {
      const ev: ReadyEvent = cfg.videoReadyEvent ?? "loadedmetadata";

      videos.forEach((src) => {
        const v = document.createElement("video");

        v.preload = "metadata";

        v.muted = true;
        v.playsInline = true;
        (v as any).disableRemotePlayback = true;

        let once = false;
        const onDone = () => {
          if (once) return;
          once = true;
          done += 1;
          check();
        };

        v.addEventListener(ev, onDone, { once: true });
        v.addEventListener("error", onDone, { once: true });

        v.src = src;
        try {
          v.load();
        } catch {
        }

        cleanups.push(() => {
          v.removeEventListener(ev, onDone);
          v.removeEventListener("error", onDone);
          try {
            v.removeAttribute("src");
            (v as any).srcObject = null;
            v.load();
          } catch {
          }
        });
      });
    }

    check();

    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
      cleanups.forEach((fn) => fn());

      if (activePathRef.current === path) {
        setSplashFlag(false);
      }
    };
  }, [location.pathname]);

  useEffect(() => {
    if (!showSplash) return;
    const t = window.setTimeout(() => {
      setShowSplash(false);
      setSplashFlag(false);
    }, 6000);
    return () => window.clearTimeout(t);
  }, [showSplash]);

  return (
    <>
      <Navbar />
      <div className="content">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/projects" element={<ProjectsPage />} />
          <Route path="/projects/:id" element={<ProjectDetails />} />
          <Route path="/about" element={<About />} />
          <Route path="/aktuelles" element={<NewsPage />} />
          <Route path="/aktuelles/:slug" element={<NewsDetailPage />} />
          <Route path="/jobs" element={<Jobs />} />
<Route path="/jobs/:id" element={<JobDetails />} />
          <Route path="/dienstleistungen" element={<Dienstleistungen />} />
          <Route
            path="/dienstleistungen/:id"
            element={<DienstleistungDetail />}
          />
          <Route path="/contact" element={<Contact />} />
          <Route path="/impressum" element={<Impressum />} />
          <Route path="/datenschutz" element={<Datenschutz />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </div>
      <Footer />
      <FloatingButtons />
      {showSplash && <SplashScreen mode={splashMode} />}
    </>
  );
};

export default AppContent;
